import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, Globe, Search, X } from 'lucide-react';
import {
  SUPPORTED_LANGUAGES,
  getLanguageInfo,
  loadLanguage,
  type LanguageCode,
} from '@/i18n';
import { reloadManifest } from '@/lib/pwa-manifest';
import { Flag } from './Flag';

// Slide-up sheet language picker.
//
// Two scopes:
//   - 'marketing' → 5 hand-picked languages (HR/EN/SL/DE/IT). Used on
//     pre-launch + marketing surfaces where we only have copy in those
//     five and want a clean focused list.
//   - 'global'    → all 28 supported languages with grouped sections and
//     a search box. Used on Guest Portal + Dashboard where the user is
//     already inside the app and may be a guest from anywhere in the world.
//
// We render through a fixed overlay rather than a Portal because the design
// already nests modals (BookingScanModal etc.) without one and the bg blur
// works fine without Portal-isolating stacking contexts.

const MARKETING_CODES = ['hr', 'en', 'sl', 'de', 'it'] as const;
type MarketingCode = (typeof MARKETING_CODES)[number];

interface LanguageModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Marketing modal stays in 5 langs we have full copy for. Global lists all
   * 28 with search + region grouping.
   */
  scope?: 'marketing' | 'global';
  /**
   * Page-local override. Most of the app uses i18n, but the teaser pages
   * track their own per-page lang state because they render copy from a
   * static bundle, not i18n. When `currentLang` and `onSelect` are passed,
   * we drive selection through them and skip i18n.changeLanguage entirely.
   */
  currentLang?: string;
  onSelect?: (code: string) => void;
  /**
   * Called after the UI language actually changed (standard i18n path) —
   * the dashboard uses it to remember the choice in profiles.language.
   */
  onLanguageChanged?: (code: string) => void;
}

const SHEET_VARIANT_TITLE: Record<'marketing' | 'global', { hr: string; en: string }> = {
  marketing: { hr: 'Odaberite jezik', en: 'Select language' },
  global: { hr: 'Odaberite jezik', en: 'Select language' },
};

const SHEET_VARIANT_HINT: Record<'marketing' | 'global', { hr: string; en: string }> = {
  marketing: {
    hr: 'Više jezika dolazi pri lansiranju',
    en: 'More languages arrive at launch',
  },
  global: {
    hr: 'Pretraži ili odaberi sa popisa',
    en: 'Search or pick from the list',
  },
};

export function LanguageModal({
  open,
  onClose,
  scope = 'marketing',
  currentLang,
  onSelect,
  onLanguageChanged,
}: LanguageModalProps) {
  const { i18n } = useTranslation();
  const activeCode = currentLang ?? i18n.language;
  const sheetRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const lang = i18n.language === 'hr' ? 'hr' : 'en';

  // Esc closes; clicking the backdrop closes; tab is trapped inside the sheet.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Lock body scroll while the sheet is up so the page underneath doesn't
    // bounce when the user scrolls a long language list on mobile.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Auto-focus the search box on the global variant. Skip on marketing
  // because it has no search input, and skip on mobile so we don't pop the
  // keyboard the moment the user opens the picker (they probably want to
  // tap a flag, not type).
  useEffect(() => {
    if (!open || scope !== 'global') return;
    const isCoarse =
      typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
    if (isCoarse) return;
    setTimeout(() => searchRef.current?.focus(), 80);
  }, [open, scope]);

  // Reset search when reopening so the user doesn't see stale filter state.
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const marketingLangs = useMemo(() => {
    return MARKETING_CODES.map((code) => getLanguageInfo(code)).filter(
      (info): info is NonNullable<ReturnType<typeof getLanguageInfo>> => Boolean(info),
    );
  }, []);

  // App languages (HR/EN) are pinned at the very top of the global picker so
  // they're always visible and can never scroll out of view — that was the
  // bug where users couldn't find Croatian/English in the long list.
  const mainLangs = useMemo(
    () => SUPPORTED_LANGUAGES.filter((l) => l.region === 'main'),
    [],
  );

  // Filtered list (everything except the pinned main langs). Empty query →
  // all non-main langs grouped by region; non-empty → flat search over ALL.
  const filteredGlobal = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SUPPORTED_LANGUAGES.filter((l) => l.region !== 'main');
    return SUPPORTED_LANGUAGES.filter(
      (l) =>
        l.code.toLowerCase().includes(q) ||
        l.name.toLowerCase().includes(q) ||
        l.nativeName.toLowerCase().includes(q),
    );
  }, [query]);

  const handleSelect = async (code: string) => {
    // Caller-controlled (teaser pages): just notify, they handle i18n.
    if (onSelect) {
      onSelect(code);
      onClose();
      return;
    }
    // Standard i18n path: lazy-load the bundle if needed, then switch.
    // Doing the await before changeLanguage avoids a flash of the EN
    // fallback for the few hundred ms while the JSON loads on first use.
    if (!['hr', 'en'].includes(code)) {
      await loadLanguage(code);
    }
    i18n.changeLanguage(code);
    reloadManifest();
    onLanguageChanged?.(code);
    onClose();
  };

  if (!open) return null;

  const title = SHEET_VARIANT_TITLE[scope][lang];
  const hint = SHEET_VARIANT_HINT[scope][lang];

  // Render through a Portal into <body>. Some call sites (the dashboard
  // Header) live inside an element with `backdrop-filter`, which establishes
  // a containing block for `position: fixed` descendants — that pinned the
  // overlay to the header instead of the viewport, so the picker showed up as
  // a stray list under the globe icon rather than a centered sheet. Portaling
  // to <body> escapes that containing block on every surface.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Backdrop. Click closes. We deliberately keep this on its own element
          so the sheet itself can stop click propagation without intercepting
          legitimate inside-clicks. */}
      <button
        type="button"
        aria-label={lang === 'hr' ? 'Zatvori' : 'Close'}
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm cs-fade-up"
      />

      <div
        ref={sheetRef}
        className="relative flex flex-col w-full h-full sm:h-auto sm:max-h-[min(85vh,720px)] sm:max-w-md mx-auto bg-[hsl(var(--background))]/95 backdrop-blur-2xl sm:border border-[hsl(var(--border))] sm:rounded-2xl shadow-2xl overflow-x-hidden cs-slide-up"
        // Mobile: fill the whole screen (h-full from the inset-0 parent) and
        // pad the top for the status-bar/notch safe area.
        // Desktop: auto-height card capped so a 28-language list never pushes
        // the close button off-viewport.
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        {/* Header */}
        <div className="px-5 sm:px-6 pt-4 sm:pt-6 pb-4 flex items-start gap-3 border-b border-[hsl(var(--border))]">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
            <Globe className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-lg leading-tight">{title}</h2>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{hint}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={lang === 'hr' ? 'Zatvori' : 'Close'}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Pinned app languages (HR/EN) — always visible at the top so they
            never scroll out of view. Hidden while searching. Global only. */}
        {scope === 'global' && !query.trim() && (
          <div className="px-5 sm:px-6 pt-4">
            <div className="grid grid-cols-2 gap-2">
              {mainLangs.map((l) => {
                const isActive = activeCode === l.code;
                return (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => handleSelect(l.code)}
                    className={`flex items-center gap-2.5 px-3 py-3 rounded-xl text-left transition-all border ${
                      isActive
                        ? 'bg-gradient-to-br from-cyan-500/15 to-blue-500/10 border-cyan-500/40 ring-1 ring-cyan-500/30'
                        : 'border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]'
                    }`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <Flag languageCode={l.code} size="xl" />
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold text-sm text-[hsl(var(--foreground))] truncate">
                        {l.nativeName}
                      </span>
                      <span className="block text-[11px] text-[hsl(var(--muted-foreground))] truncate">
                        {l.name}
                      </span>
                    </span>
                    {isActive && <Check className="h-4 w-4 text-cyan-500 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Optional search (global only) */}
        {scope === 'global' && (
          <div className="px-5 sm:px-6 pt-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={lang === 'hr' ? 'Pretraži jezik...' : 'Search language...'}
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-[hsl(var(--muted))]/50 border border-[hsl(var(--border))] text-sm focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors"
              />
            </div>
          </div>
        )}

        {/* Language list — flex-1 so it fills the remaining height (full screen
            on mobile, capped card on desktop) and scrolls internally.
            scrollbar-hide keeps the native scrollbar out of the way (it looked
            wrong stuck to the edge on mobile). */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-hide px-3 sm:px-4 py-3">
          {scope === 'marketing' ? (
            <ul className="grid grid-cols-1 gap-1.5">
              {marketingLangs.map((l) => {
                const isActive = (activeCode as MarketingCode) === l.code;
                return (
                  <li key={l.code}>
                    <button
                      type="button"
                      onClick={() => handleSelect(l.code)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                        isActive
                          ? 'bg-gradient-to-r from-cyan-500/15 to-blue-500/10 ring-1 ring-cyan-500/30'
                          : 'hover:bg-[hsl(var(--muted))]'
                      }`}
                      aria-current={isActive ? 'true' : undefined}
                    >
                      <Flag languageCode={l.code} size="xl" />
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium text-[hsl(var(--foreground))]">
                          {l.nativeName}
                        </span>
                        <span className="block text-xs text-[hsl(var(--muted-foreground))]">
                          {l.name}
                        </span>
                      </span>
                      {isActive && (
                        <Check className="h-5 w-5 text-cyan-500 shrink-0" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <GlobalList
              items={filteredGlobal}
              activeCode={activeCode}
              query={query}
              onSelect={handleSelect}
              emptyLabel={lang === 'hr' ? 'Nema rezultata' : 'No results'}
            />
          )}
        </div>
      </div>

      {/* Local CSS for the sheet animations. We keep them inline so the
          modal works wherever it's used without needing the consumer page
          to ship matching keyframes. cs-fade-up / cs-slide-up don't conflict
          with the ones used by ComingSoonPage because we scope them via the
          unique animation names below. */}
      <style>{`
        @keyframes lm-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes lm-slide-up {
          from { transform: translateY(20px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
        .cs-fade-up { animation: lm-fade 200ms ease-out; }
        .cs-slide-up { animation: lm-slide-up 280ms cubic-bezier(0.22, 1, 0.36, 1); }
        @media (prefers-reduced-motion: reduce) {
          .cs-fade-up, .cs-slide-up { animation: none; }
        }
      `}</style>
    </div>,
    document.body,
  );
}

interface GlobalListProps {
  items: ReadonlyArray<(typeof SUPPORTED_LANGUAGES)[number]>;
  activeCode: string;
  query: string;
  onSelect: (code: LanguageCode | string) => void;
  emptyLabel: string;
}

function GlobalList({ items, activeCode, query, onSelect, emptyLabel }: GlobalListProps) {
  if (items.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-[hsl(var(--muted-foreground))]">
        {emptyLabel}
      </div>
    );
  }

  // When the user is searching, show a flat list — region headings get in
  // the way of "I just want to find Polish quickly". When idle, group by
  // region so the full list isn't a wall of 28 flags.
  if (query.trim()) {
    return (
      <ul className="grid grid-cols-1 gap-1">
        {items.map((l) => (
          <LangRow key={l.code} lang={l} active={activeCode === l.code} onSelect={onSelect} />
        ))}
      </ul>
    );
  }

  const sections: Array<{
    region: 'main' | 'europe' | 'world';
    title: { hr: string; en: string };
  }> = [
    { region: 'main', title: { hr: 'Glavni jezici', en: 'Main' } },
    { region: 'europe', title: { hr: 'Europa', en: 'Europe' } },
    { region: 'world', title: { hr: 'Svijet', en: 'World' } },
  ];

  return (
    <div className="space-y-4">
      {sections.map(({ region, title }) => {
        const groupItems = items.filter((l) => l.region === region);
        if (groupItems.length === 0) return null;
        return (
          <div key={region}>
            <h3 className="px-2 mb-1.5 text-[11px] uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">
              {title.en}
            </h3>
            <ul className="grid grid-cols-1 gap-1">
              {groupItems.map((l) => (
                <LangRow
                  key={l.code}
                  lang={l}
                  active={activeCode === l.code}
                  onSelect={onSelect}
                />
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function LangRow({
  lang,
  active,
  onSelect,
}: {
  lang: (typeof SUPPORTED_LANGUAGES)[number];
  active: boolean;
  onSelect: (code: string) => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(lang.code)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
          active
            ? 'bg-gradient-to-r from-cyan-500/15 to-blue-500/10 ring-1 ring-cyan-500/30'
            : 'hover:bg-[hsl(var(--muted))]'
        }`}
        aria-current={active ? 'true' : undefined}
      >
        <Flag languageCode={lang.code} size="lg" />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-[hsl(var(--foreground))]">
            {lang.nativeName}
          </span>
          <span className="block text-[11px] text-[hsl(var(--muted-foreground))]">
            {lang.name}
          </span>
        </span>
        {active && <Check className="h-4 w-4 text-cyan-500 shrink-0" />}
      </button>
    </li>
  );
}

export default LanguageModal;
